import {Trans, useTranslation} from "react-i18next";
import {useRouteMatch} from "react-router-dom";
import {css} from "@emotion/react";
import {Text} from "@pg-design/text";

import {mapCitySlugToRegionType} from "../../../../../config/regions";
import {useAppSelector} from "../../../../../utils/hooks/store_hooks";
import {mapPathnameToOfferType} from "../../../common/app/routing/offer_type";
import {breakpoints} from "../../../common/app/styles/breakpoints";
import {DashboardMapChartCard} from "../../../components/charts/chart_cards/dashboard_map_chart_card/DashboardMapChartCard";
import {Grid} from "../../../components/Grid";
import {withDateGuard} from "../../../components/hoc/withDateGuard";
import {withGTM} from "../../../components/hoc/withGTM";
import {ChartsLayout as Layout} from "../../../components/layout/charts_layout/ChartsLayout";
import {MethodologyTooltip} from "../../../components/MethodologyTooltip";
import {selectAuthStatus} from "../../auth/auth_slice";
import {pageviewAlgolyticsHit} from "../../tracking/algolytics/pageview_hits";
import {usePageview} from "../../tracking/use_pageview";
import {ViewType} from "../../tracking/view_type";
import {useChartsRegionId} from "../hooks/use_charts_region_id";
import {useSelectedRegionDisplay} from "../hooks/use_selected_region_display";
import {getCityViewConfig} from "../utils/city_view_config";

export const Dashboard = withDateGuard(
    withGTM(() => {
        const {path} = useRouteMatch();
        const {t} = useTranslation();

        const {citySlug, isKnownCity, regionLabel} = useSelectedRegionDisplay();
        const regionType = mapCitySlugToRegionType(citySlug);
        const routeOfferType = mapPathnameToOfferType(path);
        const {filterOfferType, propertyType} = getCityViewConfig(routeOfferType);

        usePageview(() => {
            pageviewAlgolyticsHit({citySlug, offerType: routeOfferType, regionType, viewType: ViewType.DASHBOARD});
        });

        const {isAuthorized} = useAppSelector(selectAuthStatus);
        const {hasChartsAccess, isRegionIdReady} = useChartsRegionId();
        const isPermitted = isAuthorized && hasChartsAccess;

        const mapOfferTypeProps = filterOfferType ? {offerType: filterOfferType} : {};

        return (
            <Layout>
                <Grid type="dashboard">
                    <div css={mapHeadline}>
                        <Text variant="headline_5" as="h2">
                            {t("dashboard.investments_map_headline")}
                        </Text>

                        <MethodologyTooltip methodology={false} content={t("dashboard.investments_map_tooltip")} />
                    </div>

                    <DashboardMapChartCard
                        city={citySlug}
                        title={
                            <Trans>
                                {isKnownCity
                                    ? t("dashboard.map.title", {propertyType, city: citySlug})
                                    : t("dashboard.map.title_region", {propertyType, region: regionLabel})}
                            </Trans>
                        }
                        helper={<Trans>{t("dashboard.map.description", {propertyType, city: citySlug})}</Trans>}
                        locked={isRegionIdReady && !isPermitted}
                        empty={!isRegionIdReady}
                        {...mapOfferTypeProps}
                    />
                </Grid>
            </Layout>
        );
    })
);

export const mapHeadline = css`
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.5rem 0;

    @media (min-width: ${breakpoints.md}) {
        grid-area: mapHeadline;
        padding: 1.5rem 0;
    }
`;
